import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["loadingContainer", "contentContainer"];

  static values = {
    bookingId: String,
    groupId: String,
    accommodationId: String,
    roomAssignmentId: String,
    roomIndex: Number,
    adults: Number,
    kids: Number,
    babies: Number,
    availableRoomsCountText: String,
    onlyXAvailableText: String,
    availableText: String,
    currentlyUnavailableText: String,
    selectButtonText: String,
    availabilityErrorText: String,
  };

  connect() {
    console.log(
      "[ProductGroupProfileAvailabilityLoader] Connected for accommodation:",
      this.accommodationIdValue
    );

    // For rooms with specific occupancy (room assignments), load independently
    if (this.hasAdultsValue) {
      console.log(
        "[ProductGroupProfileAvailabilityLoader] Loading with specific occupancy:",
        this.adultsValue,
        "adults"
      );
      this.loadAvailability();
    } else {
      // For general loading (no room assignments), use shared loading
      if (!window.productGroupProfileAvailabilityLoaderInstances) {
        window.productGroupProfileAvailabilityLoaderInstances = {};
      }
      if (
        !window.productGroupProfileAvailabilityLoaderInstances[
          this.groupIdValue
        ]
      ) {
        window.productGroupProfileAvailabilityLoaderInstances[
          this.groupIdValue
        ] = [];
      }
      window.productGroupProfileAvailabilityLoaderInstances[
        this.groupIdValue
      ].push(this);

      if (!window.productGroupProfileAvailabilityLoadingForGroup) {
        window.productGroupProfileAvailabilityLoadingForGroup = {};
      }

      if (
        !window.productGroupProfileAvailabilityLoadingForGroup[
          this.groupIdValue
        ]
      ) {
        window.productGroupProfileAvailabilityLoadingForGroup[
          this.groupIdValue
        ] = true;
        this.loadAvailability();
      }
    }
  }

  disconnect() {
    // Clean up instance reference
    if (
      window.productGroupProfileAvailabilityLoaderInstances?.[this.groupIdValue]
    ) {
      const index =
        window.productGroupProfileAvailabilityLoaderInstances[
          this.groupIdValue
        ].indexOf(this);
      if (index > -1) {
        window.productGroupProfileAvailabilityLoaderInstances[
          this.groupIdValue
        ].splice(index, 1);
      }
    }
  }

  async loadAvailability() {
    try {
      console.log(
        "[ProductGroupProfileAvailabilityLoader] Loading availability for group:",
        this.groupIdValue
      );

      let url = `/${this.getLocale()}/frontoffice/api/bookings/${
        this.bookingIdValue
      }/resource_group_availability?group_id=${this.groupIdValue}`;

      // Add occupancy parameters if this is for a specific room assignment
      if (this.hasAdultsValue) {
        url += `&adults=${this.adultsValue}&kids=${
          this.kidsValue || 0
        }&babies=${this.babiesValue || 0}`;
      }

      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(
        "[ProductGroupProfileAvailabilityLoader] Received data:",
        data
      );

      // Broadcast the data to all accommodation containers
      this.broadcastAvailability(data);
    } catch (error) {
      console.error(
        "[ProductGroupProfileAvailabilityLoader] Error loading availability:",
        error
      );
      this.showError();
    }
  }

  broadcastAvailability(data) {
    // For occupancy-specific loads, only update self
    if (this.hasAdultsValue) {
      this.handleAvailabilityData(data);
      return;
    }

    // For general loads, broadcast to all instances
    console.log(
      "[ProductGroupProfileAvailabilityLoader] Broadcasting to all instances for group:",
      this.groupIdValue
    );

    if (
      window.productGroupProfileAvailabilityLoaderInstances?.[this.groupIdValue]
    ) {
      window.productGroupProfileAvailabilityLoaderInstances[
        this.groupIdValue
      ].forEach((instance) => {
        if (instance !== this) {
          instance.handleAvailabilityData(data);
        }
      });
    }

    this.handleAvailabilityData(data);
  }

  handleAvailabilityData(data) {
    if (!data.accommodations) return;

    // Find the accommodation data for this specific accommodation
    const accommodationData = data.accommodations.find(
      (acc) => acc.accommodation_id.toString() === this.accommodationIdValue
    );

    if (!accommodationData) {
      console.warn(
        "[ProductGroupProfileAvailabilityLoader] No data found for accommodation:",
        this.accommodationIdValue
      );
      return;
    }

    this.updateUI(accommodationData);
  }

  updateUI(accommodationData) {
    // Hide loading indicator
    if (this.hasLoadingContainerTarget) {
      this.loadingContainerTarget.style.display = "none";
    }

    // Show and populate content
    if (this.hasContentContainerTarget) {
      this.contentContainerTarget.style.display = "block";
      this.renderRooms(accommodationData);
    }
  }

  renderRooms(accommodationData) {
    const {
      available_rooms: availableRooms,
      unavailable_rooms: unavailableRooms,
    } = accommodationData;

    let html = "";

    // Render available rooms section
    if (availableRooms && availableRooms.length > 0) {
      html += '<div class="p-4">';
      html += `<h4 class="text-sm font-medium text-green-700 mb-4">${this.availableRoomsCountTextValue.replace(
        "%{count}",
        availableRooms.length
      )}</h4>`;
      html += '<div class="space-y-3">';
      availableRooms.forEach((room) => {
        html += this.renderRoomOption(room);
      });
      html += "</div></div>";
    }

    // Render unavailable rooms section
    if (unavailableRooms && unavailableRooms.length > 0) {
      const roomNames = unavailableRooms.map((r) => r.name).join(", ");
      html += `
        <div class="px-4 pb-4">
          <div class="border-t border-gray-200 pt-3">
            <h6 class="text-xs font-medium text-gray-500 mb-2">${this.currentlyUnavailableTextValue}</h6>
            <div class="text-xs text-gray-400">${roomNames}</div>
          </div>
        </div>
      `;
    }

    if (this.hasContentContainerTarget) {
      this.contentContainerTarget.innerHTML = html;
    }
  }

  renderRoomOption(room) {
    const roomAssignmentData = this.hasRoomAssignmentIdValue
      ? `data-room-assignment-id="${this.roomAssignmentIdValue}" data-room-index="${this.roomIndexValue}"`
      : `data-sku-id="${room.id}" data-group-id="${this.groupIdValue}"`;

    // Determine availability badge based on quantity
    let availabilityBadge = "";
    if (room.available_quantity !== "unlimited") {
      const quantity = parseInt(room.available_quantity, 10);
      const badgeClass =
        quantity <= 3
          ? "text-red-600 bg-red-50"
          : "text-yellow-600 bg-yellow-50";
      const quantityText = this.onlyXAvailableTextValue.replace(
        "%{quantity}",
        quantity
      );

      availabilityBadge = `
        <div class="inline-flex items-center text-xs mt-1 mb-1 font-medium px-2 py-1 rounded-full ${badgeClass}">
          <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
          </svg>
          ${quantityText}
        </div>
      `;
    } else {
      availabilityBadge = `
        <div class="inline-flex items-center text-xs text-green-700 bg-green-100 mt-1 mb-1 font-medium px-2 py-1 rounded-full">
          <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
          </svg>
          ${this.availableTextValue}
        </div>
      `;
    }

    const descriptionHtml = room.description
      ? `<p class="text-xs text-gray-600 mt-1 line-clamp-2">${this.escapeHtml(
          room.description
        )}</p>`
      : "";

    return `
      <div class="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors">
        <div class="p-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <div class="flex-1 min-w-0">
            <div>
              <h6 class="font-medium text-gray-900 text-sm">${this.escapeHtml(
                room.name
              )}</h6>
              ${availabilityBadge}
              ${descriptionHtml}
            </div>
          </div>
          <div class="flex items-center justify-center sm:justify-end">
            <button 
              type="button"
              class="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-action="click->product-group-profile-accommodation#selectRoom"
              data-room-id="${room.id}"
              data-accommodation-id="${this.accommodationIdValue}"
              ${roomAssignmentData}>
              ${this.selectButtonTextValue}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // eslint-disable-next-line class-methods-use-this
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  showError() {
    if (this.hasLoadingContainerTarget) {
      this.loadingContainerTarget.innerHTML = `
        <div class="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-sm text-red-700">
            ${this.availabilityErrorTextValue}
          </p>
        </div>
      `;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getLocale() {
    const pathParts = window.location.pathname.split("/");
    return pathParts[1] || "de";
  }
}
